"use client";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react"
import { Plus, Minus } from "lucide-react"
import { addToCart, decreaseQuantity, increaseQuantity } from "../config/redux/slices/cartSlice";
import { toast } from "react-toastify";
import { event } from '@/lib/gtag';


const AddToCartButton = ({ option, selectedSubcategory, selectedSubcategoryId, Discount, offering, showUnitLabel = true }) => {
  const dispatch = useDispatch();
  const cart = useSelector(state => state.cart.items);
  const cartItem = cart.find(item => item.id === option.id);
  const isFull = option?.status === "full";
  const isAvailable = option?.status === "available";
  
  // Animation states
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('up'); // 'up' or 'down'
  const prevQuantityRef = useRef(cartItem?.quantity || 0);

  // Sync animation across all instances when Redux quantity changes
  useEffect(() => {
    const currentQty = cartItem?.quantity || 0;
    const prevQty = prevQuantityRef.current;
    
    if (currentQty !== prevQty && prevQty !== 0 && currentQty !== 0) {
      // Quantity changed, trigger animation
      setAnimationDirection(currentQty > prevQty ? 'up' : 'down');
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 200);
    }
    
    prevQuantityRef.current = currentQty;
  }, [cartItem?.quantity]);

 const labelMap = {
    Utensils: "utensil",
    Bathroom: "bathrooms",
    Staircase: "floors",
    "Fan Cleaning": "fans",
    "Window Cleaning": "windows",
    Balcony: "balconies",
    "Water Tank Cleaning": "tanks",
    "Kitchen Prep": "minutes",
    "Kitchen": "kitchen",
    "Fridge Cleaning": "fridge",
    "Sofa Cleaning": "sofa",
    "Carpet Cleaning": "carpet",
    "Roof Top Cleaning": "roof",
    "30 Min Express":"30 mins",
    "1 Hour Standard":"1 hour",
    "1.5 Hour Plus":"1.5 hour",
    "2 Hour Routine":"2 hours",
   "3 Hour Complete":"3 hours",
    "Kitchen Deep Cleaning" :"kitchens",
   "Bathroom Deep Cleaning" :"bathrooms",
   "Bedroom Deep Cleaning" :"bedrooms",
   "Dinning room  Deep Cleaning" :"rooms",
   "Cabients Deep Cleaning" :"kitchen",
   "Almirah Deep Cleaning" :"almirahs",
   "Balcony Deep Cleaning" :"balconies",
   "Rooftop Deep Cleaning" :"rooftops",
   "Front Yard Deep Cleaning" :"frontyards",
    "Backyard Deep Cleaning" :"backyards",
    "Chimney Deep Cleaning" :"chimneys",
    "Cabinets Deep Cleaning":"cabinets",
   
    "Hatchback / Sedan":"smallcar",
    "SUV / MUV":"bigcar",
    "Luxury Car":"car",
    "Truck / Bus":"vehicle",
    "Bike / Scooter":"bike"
  };
  
  const hourlyOptions = [
    "30 Min Express",
  "1 Hour Standard",
  "1.5 Hour Plus",
  "2 Hour Routine",
  "3 Hour Complete",
];
const isHourly = hourlyOptions.includes(option.name);

  // Check if cart already has a different hourly service (only one hourly allowed)
  const hasDifferentHourlyInCart = cart.some(item => 
    hourlyOptions.includes(item.name) && item.id !== option.id
  );
  
  // Determine if this hourly option is blocked
  const isBlocked = isHourly && hasDifferentHourlyInCart;

  // Car wash options that have max quantity of 3
  const carWashOptions = [
    "Hatchback / Sedan",
    "SUV / MUV",
    "Luxury Car",
    "Truck / Bus"
  ];
  const bikeWashOptions = [
    "Bike / Scooter"
  ];
  const isBikeWash = bikeWashOptions.includes(option.name);
  const isCarWash = carWashOptions.includes(option.name);

  // Check if option is Utensils (max quantity 110)
  const isUtensils = option.name === "Utensils";

  // Check if max quantity is reached
  const isMaxQuantity = (
    (isCarWash && cartItem?.quantity >= 3) ||
    (isBikeWash && cartItem?.quantity >= 5) ||
    (isUtensils && cartItem?.quantity >= 110)
  );
 
  useEffect(() => {
    if (cart && cart.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('cart');
    }
  }, [cart]);

  return (
    <>
      <div className={`${(!cartItem || cartItem.quantity === 0) ? 'z-10 bg-transparent' : 'z-10 border border-[#00489b] rounded-[6px] bg-white border-solid'}`}>
        {(!cartItem || cartItem.quantity === 0) ? (
          // Show "+" icon button when not in cart or quantity is zero
         <button
  onClick={() => {
    if (isBlocked) {
      toast.error(
        "Only one hourly service can be added at a time. Please remove the existing hourly service first."
      );
      return;
    }

    if (!isFull) {
      dispatch(
        addToCart({
          ...option,
          selectedSubcategory,
          Discount,
          offering,
          selectedSubcategoryId,
          serviceType: option?.serviceType,
          slug: option?.slug,
          categoryRef: option?.categoryRef,
        })
      );
      // Track add to cart event
      event({
        action: 'add_to_cart',
        category: 'sortkaro',
        label: option.name,
        value: option.price
      });
    }
  }}
  disabled={isFull || isBlocked}
  className={`
    rounded-[8px] font-bold flex items-center justify-center w-8 h-8 border-2
    ${
      isFull || isBlocked
        ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
        : 'bg-white border-[#00489b] text-[#00489b]'
    }
  `}
  title={isBlocked ? "Cannot add more than one Hourly service at a time" : ""}
>
 
    <Plus className="w-5 h-5" />
  
   
</button>

        ) : (
          // Show increment-decrement UI if already in cart and quantity > 0
          <>
            <div className="flex flex-col items-center rounded-[12px] justify-center relative animate-in fade-in duration-300">
              {/* Top row with - qty + */}
              <div className="flex items-center justify-center w-[95px] h-7">
                <button
                  onClick={() => {
                    dispatch(decreaseQuantity(option.id));
                    // Track decrease quantity event
                    event({
                      action: 'decrease_quantity',
                      category: 'sortkaro',
                      label: option.name
                    });
                  }}
                  disabled={isFull}
                  className="w-5 h-5 rounded-md text-[#00489b] font-bold flex items-center justify-center transition-transform duration-150 active:scale-75 hover:bg-blue-50"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <div className="relative w-8 h-6 overflow-hidden">
                  <span 
                    className={`absolute inset-0 font-bold text-[15px] text-[#00489b] flex items-center justify-center transition-all duration-200 ${
                      isAnimating 
                        ? animationDirection === 'up' 
                          ? '-translate-y-full opacity-0' 
                          : 'translate-y-full opacity-0'
                        : 'translate-y-0 opacity-100'
                    }`}
                  >
                    {cartItem.quantity}
                  </span>
                </div>
              <button
                onClick={() => {
                  if (isMaxQuantity) {
                    if (isUtensils) {
                      toast.error("Maximum quantity of 110 utensils reached.");
                    } else if (isBikeWash) {
                      toast.error("Maximum quantity of 5 reached for Bike / Scooter.");
                    } else {
                      toast.error("Maximum quantity of 3 reached for this item.");
                    }
                    return;
                  }
                  if (isHourly) {
                    toast.error("Only one hourly service can be added at a time.");
                    return;
                  }
                  if (!isFull) {
                    dispatch(increaseQuantity(option.id));
                    // Track increase quantity event
                    event({
                      action: 'increase_quantity',
                      category: 'sortkaro',
                      label: option.name
                    });
                  }
                }}
                disabled={isHourly || isFull || isMaxQuantity}
                className={`w-5 h-5 rounded-md font-bold flex items-center justify-center transition-transform duration-150 active:scale-75
                  ${(isHourly || isMaxQuantity) ? "opacity-40 cursor-not-allowed text-gray-400" : "text-[#00489b] hover:bg-blue-50"}`}
              >
                <Plus className="w-5 h-5" />
              </button>

              </div>
              {showUnitLabel && (
                <div className="flex justify-center items-center text-center absolute -bottom-2 font-bold text-[10px] bg-white text-[#666] px-[2px]">
                  {labelMap[option.name] || "rooms"}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};
export default AddToCartButton;